import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 历史时间轴动画
 * 
 * 核心效果：
 * 1. 展示历史事件的时间顺序
 * 2. 支持事件卡片、年代标记
 * 3. 水平滚动、时间节点、事件展开
 * 4. 适合：历史教学、年代记忆
 */

interface HistoricalEvent {
  year: string;
  title: string;
  description: string;
  icon?: string;
  category?: string;
}

interface HistoricalTimelineProps {
  events?: HistoricalEvent[];
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  title?: string;
  subtitle?: string;
}

const DEFAULT_EVENTS: HistoricalEvent[] = [
  {
    year: '221 BC',
    title: '秦统一六国',
    description: '秦始皇建立中国第一个大一统王朝',
    icon: '🐉',
    category: '政治',
  },
  {
    year: '105 AD',
    title: '蔡伦改进造纸术',
    description: '四大发明之一，推动文明传播',
    icon: '📜',
    category: '科技',
  },
  {
    year: '618 AD',
    title: '唐朝建立',
    description: '中国历史上最强盛的朝代之一',
    icon: '🏮',
    category: '政治',
  },
  {
    year: '1405',
    title: '郑和下西洋',
    description: '世界航海史上的壮举',
    icon: '⛵',
    category: '航海',
  },
  {
    year: '1666',
    title: '牛顿发现万有引力',
    description: '开启经典物理学新时代',
    icon: '🍎',
    category: '科学',
  },
  {
    year: '1789',
    title: '法国大革命',
    description: '自由、平等、博爱的理念传播',
    icon: '⚔️',
    category: '政治',
  },
  {
    year: '1969',
    title: '人类登月',
    description: '阿波罗11号，人类的一大步',
    icon: '🌙',
    category: '航天',
  },
];

const DEFAULT_PROPS: Required<HistoricalTimelineProps> = {
  events: DEFAULT_EVENTS,
  primaryColor: '#00d9ff',
  secondaryColor: '#ff6b6b',
  accentColor: '#ffd93d',
  backgroundColor: '#0a0e27',
  title: '人类文明历程',
  subtitle: 'Major Events in Human History',
};

export const HistoricalTimelineAnimation: React.FC<HistoricalTimelineProps> = (props) => {
  const {
    events,
    primaryColor,
    secondaryColor,
    accentColor,
    backgroundColor,
    title,
    subtitle,
  } = { ...DEFAULT_PROPS, ...props };
  
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // 时间轴出现动画
  const timelineOpacity = interpolate(frame, [40, 70], [0, 1], { extrapolateRight: 'clamp' });
  
  // 时间轴滚动进度
  const scrollProgress = interpolate(frame, [80, 420], [0, 1], { extrapolateRight: 'clamp' });
  
  // 计算时间轴总宽度
  const cardWidth = 320;
  const cardGap = 40;
  const totalWidth = events.length * (cardWidth + cardGap);
  const viewWidth = width - 200;
  const maxScroll = Math.max(0, totalWidth - viewWidth);
  const currentScroll = scrollProgress * maxScroll;

  // 背景时间标记动画
  const yearMarkerOpacity = interpolate(frame, [60, 90], [0, 1], { extrapolateRight: 'clamp' });

  // 当前聚焦事件索引
  const focusIndex = Math.floor(scrollProgress * events.length);

  return (
    <AbsoluteFill style={{
      background: backgroundColor,
      fontFamily: 'Inter, system-ui, sans-serif',
      color: '#e5e7eb',
      padding: 60,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* 标题 */}
      <div style={{
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `scale(${titleScale})`,
        marginBottom: 40,
      }}>
        <h1 style={{
          fontSize: 72,
          fontWeight: 800,
          margin: 0,
          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: 2,
        }}>
          {title}
        </h1>
        <p style={{
          fontSize: 28,
          color: '#94a3b8',
          marginTop: 12,
        }}>
          {subtitle}
        </p>
      </div>

      {/* 背景年代标记 */}
      <div style={{
        position: 'absolute',
        top: 220,
        left: 0,
        right: 0,
        bottom: 60,
        overflow: 'hidden',
        opacity: yearMarkerOpacity * 0.15,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          fontSize: 400,
          fontWeight: 900,
          color: primaryColor,
          letterSpacing: -10,
        }}>
          TIMELINE
        </div>
      </div>

      {/* 时间轴容器 */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        margin: '0 -60px',
        padding: '0 60px',
      }}>
        {/* 时间轴主线 */}
        <div style={{
          position: 'absolute',
          left: 100,
          right: 100,
          top: '50%',
          height: 4,
          background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
          opacity: timelineOpacity,
          borderRadius: 2,
        }} />

        {/* 事件卡片容器 */}
        <div style={{
          display: 'flex',
          gap: cardGap,
          transform: `translateX(${-currentScroll}px)`,
          transition: 'transform 0.05s ease-out',
          paddingLeft: 100,
          paddingRight: 100,
        }}>
          {events.map((event, index) => {
            // 每个卡片的延迟动画
            const cardDelay = index * 15;
            const cardOpacity = interpolate(
              frame,
              [80 + cardDelay, 110 + cardDelay],
              [0, 1],
              { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
            );
            
            const cardY = interpolate(
              frame,
              [80 + cardDelay, 120 + cardDelay],
              [50, 0],
              { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
            );

            // 是否为当前聚焦事件
            const isFocused = index === focusIndex;
            const scale = isFocused ? 1.05 : 1;

            return (
              <div
                key={index}
                style={{
                  minWidth: cardWidth,
                  opacity: cardOpacity,
                  transform: `translateY(${cardY}px) scale(${scale})`,
                }}
              >
                {/* 时间节点标记 */}
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: -20,
                  transform: 'translateX(-50%)',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: isFocused ? accentColor : primaryColor,
                  border: `3px solid ${backgroundColor}`,
                  boxShadow: `0 0 20px ${isFocused ? accentColor : primaryColor}`,
                  zIndex: 10,
                }} />

                {/* 卡片 */}
                <div style={{
                  background: isFocused 
                    ? `linear-gradient(135deg, rgba(0, 217, 255, 0.15), rgba(255, 107, 107, 0.15))`
                    : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 20,
                  padding: 30,
                  border: `2px solid ${isFocused ? accentColor : 'rgba(255, 255, 255, 0.1)'}`,
                  marginTop: 40,
                  transition: 'all 0.3s ease',
                }}>
                  {/* 年份 */}
                  <div style={{
                    fontSize: 48,
                    fontWeight: 800,
                    color: isFocused ? accentColor : primaryColor,
                    marginBottom: 15,
                    textShadow: isFocused ? `0 0 20px ${accentColor}` : 'none',
                  }}>
                    {event.year}
                  </div>

                  {/* 图标和标题 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 15,
                  }}>
                    {event.icon && (
                      <div style={{
                        fontSize: 36,
                      }}>
                        {event.icon}
                      </div>
                    )}
                    <div style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: '#ffffff',
                    }}>
                      {event.title}
                    </div>
                  </div>

                  {/* 描述 */}
                  <div style={{
                    fontSize: 24,
                    color: '#94a3b8',
                    lineHeight: 1.6,
                  }}>
                    {event.description}
                  </div>

                  {/* 分类标签 */}
                  {event.category && (
                    <div style={{
                      marginTop: 20,
                      display: 'inline-block',
                      padding: '8px 16px',
                      background: `rgba(${isFocused ? '255, 217, 61' : '0, 217, 255'}, 0.2)`,
                      borderRadius: 8,
                      fontSize: 24,
                      color: isFocused ? accentColor : primaryColor,
                      fontWeight: 600,
                    }}>
                      {event.category}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* 左侧渐隐遮罩 */}
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 100,
          background: `linear-gradient(90deg, ${backgroundColor}, transparent)`,
          pointerEvents: 'none',
        }} />

        {/* 右侧渐隐遮罩 */}
        <div style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          background: `linear-gradient(270deg, ${backgroundColor}, transparent)`,
          pointerEvents: 'none',
        }} />
      </div>

      {/* 进度指示器 */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
        marginTop: 30,
      }}>
        {/* 进度条 */}
        <div style={{
          width: 400,
          height: 8,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${scrollProgress * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`,
            borderRadius: 3,
          }} />
        </div>

        {/* 当前年份 */}
        <div style={{
          fontSize: 26,
          fontWeight: 700,
          color: accentColor,
          minWidth: 100,
        }}>
          {events[focusIndex]?.year || events[0].year}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// 默认导出
export default HistoricalTimelineAnimation;

// 导出默认事件数据供外部使用
export const exampleHistoricalEvents = DEFAULT_EVENTS;
